import { AxiosBaseQueryFunction } from 'api/axiosBaseQuery';
import { useMemo } from 'react';

import { QueryDefinition, skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import {
  UseQuery,
  UseQueryStateOptions,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';

import { ProductDetails } from '../models';

import { CommonProductQueryParams } from '~/api/easy-estimator-api/commonProductQueryParams';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectZipcode } from '~/redux/slices/zipcode';

export type TransformProductToProductDetailsCallbackType<Product> = (
  products: Product[]
) => ProductDetails[];

export type FetchProductHookReturn<
  R,
  Product,
  TransformToProductDetailsCallBackType extends
    | undefined
    | TransformProductToProductDetailsCallbackType<Product>,
> =
  R extends Record<string, never>
    ? Omit<
        TypedUseQueryHookResult<
          Product[],
          CommonProductQueryParams,
          AxiosBaseQueryFunction
        >,
        'data'
      > & {
        data: Product[];
        productsAsProductDetails: TransformToProductDetailsCallBackType extends undefined
          ? undefined
          : ProductDetails[];
      }
    : Omit<
        TypedUseQueryHookResult<
          Product[],
          CommonProductQueryParams,
          AxiosBaseQueryFunction,
          R
        >,
        'data'
      > & {
        data: Product[];
        productsAsProductDetails: TransformToProductDetailsCallBackType extends undefined
          ? undefined
          : ProductDetails[];
      };

type _QueryDefinition<Product> = QueryDefinition<
  CommonProductQueryParams,
  AxiosBaseQueryFunction,
  never,
  Product[],
  'easyEstimatorApi'
>;

type UseFetchProductsQuery<Product> = UseQuery<_QueryDefinition<Product>>;

/**
 * Creates a hook to fetch for products. Products in EE have different return types
 */
export const createFetchProductsHook = <
  Product,
  TransformToProductDetailsCallBackType extends
    | undefined
    | TransformProductToProductDetailsCallbackType<Product> = undefined,
>(createFetchProductsHooksOptions: {
  /**
   * Hook to fetch api data
   */
  apiQueryHook: UseFetchProductsQuery<Product>;
  /**
   * Callback to transform product to ProductDetails
   */
  selectProductsAsProductDetails?: TransformToProductDetailsCallBackType;
}): (<
  R extends Record<string, unknown> | Record<string, never> = Record<
    string,
    never
  >,
>(
  args?: UseQueryStateOptions<_QueryDefinition<Product>, R>
) => FetchProductHookReturn<
  R,
  Product,
  TransformToProductDetailsCallBackType
>) => {
  const { selectProductsAsProductDetails } = createFetchProductsHooksOptions;
  const emptyArray = [] satisfies Product[];

  return <R extends Record<string, unknown>>(
    args?: UseQueryStateOptions<_QueryDefinition<Product>, R>
  ) => {
    const zipCode = useAppSelector(selectZipcode);
    const selectedStore = useSelectedStore();
    const selectedColor = useAppSelector(selectSelectedColor);

    const { apiQueryHook } = createFetchProductsHooksOptions;
    const { data = emptyArray, ...rest } = apiQueryHook(
      zipCode && selectedStore?.companyId && selectedColor?.qteGrpID
        ? {
            params: {
              zipCode,
              storeId: selectedStore.companyId,
              ...(selectedColor.qteGrpID
                ? { prodQteGrpID: selectedColor.qteGrpID }
                : {}),
            },
          }
        : skipToken,
      {
        ...(args ? args : {}),
        selectFromResult: (result) => ({
          data: result.data,
          ...(args?.selectFromResult ? args?.selectFromResult(result) : {}),
        }),
      }
    );
    const productsAsProductDetails = useMemo(
      () => selectProductsAsProductDetails?.(data),
      [data]
    );

    return {
      data,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      productsAsProductDetails: productsAsProductDetails as any, // Casting as any is necessary to use conditional types. See https://github.com/microsoft/TypeScript/issues/33912;
      ...rest,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any; // Casting as any is necessary to use conditional types. See https://github.com/microsoft/TypeScript/issues/33912;
  };
};
